import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import { usePwa } from "./pwa";
import type { AgentNotice, Paged } from "./types";

interface Notices {
  unread: number;
  refresh: () => Promise<void>;
  setUnread: (n: number) => void;
}

const Ctx = createContext<Notices>({ unread: 0, refresh: async () => undefined, setUnread: () => undefined });

type AppBadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

/** Unread notification count for the bell, the tab and the installed app's icon badge. */
export function NoticesProvider({ children }: { children: ReactNode }) {
  const { signedIn } = useAuth();
  const { pushTick, online } = usePwa();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!signedIn) return setUnread(0);
    try {
      const r = await api.get<Paged<AgentNotice> & { unread: number }>("/agent/notifications", { limit: 1 });
      setUnread(r.unread);
    } catch {
      // Offline or signed out: keep the last count.
    }
  }, [signedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh, pushTick, online]);

  useEffect(() => {
    const id = setInterval(() => document.visibilityState === "visible" && void refresh(), 60_000);
    const onVisible = () => document.visibilityState === "visible" && void refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  useEffect(() => {
    const nav = navigator as AppBadgeNavigator;
    try {
      if (unread > 0) void nav.setAppBadge?.(unread).catch(() => undefined);
      else void nav.clearAppBadge?.().catch(() => undefined);
    } catch {
      // Badging API not available.
    }
    document.title = unread > 0 ? `(${unread}) Smart Safiri Agents` : "Smart Safiri Agents";
  }, [unread]);

  const value = useMemo(() => ({ unread, refresh, setUnread }), [unread, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useNotices = () => useContext(Ctx);
